import { toWorld } from "./artShapes.js";
import { PLAYER_RADIUS, WORLD } from "./constants.js";
import { rectContains } from "./geometry.js";
import { getClub } from "./clubs.js";
/**
 * El iglú: el cuarto propio de cada cuenta.
 *
 * Es la primera sala del juego cuya geometría **no** está en el catálogo. Las trece del
 * campus son dato fijo en `rooms.ts`; ésta se arma en el momento, con el piso de acá
 * —igual para todos— y los muebles que ese jugador tenga colocados, que salen de la
 * base. De ahí sale todo lo demás:
 *
 * · **No es un `RoomId`.** La unión de literales tipa `door.to` y el registro de salas,
 *   y abrirla a cualquier texto para que quepan N iglús costaría esa garantía en las
 *   trece salas de verdad. Por eso el iglú es un `RoomShape` con id de texto, y por eso
 *   no se entra por una puerta del mapa sino por el carnet: una puerta sólo sabe llevar
 *   a un id del catálogo.
 *
 * · **Se sale por una puerta NORMAL.** El único elemento que el iglú se inventa es una
 *   `kind: "puerta"` cuyo `to` es la sala real de la que vino el jugador. Nada la
 *   distingue de las demás, así que cruzarla usa el mismo camino de siempre —clic,
 *   `Msg.UseElement`, `Msg.RoomChanged`— sin una sola rama nueva en la sala. Como su id
 *   no está en `ROOM_LIST`, la sala de destino no encuentra puerta de vuelta y aterriza
 *   en su `spawn`: exactamente lo que ya hace cualquier pasaje de un solo sentido.
 *
 * · **El mueble es un `ElementDef` de verdad.** No hay un tipo paralelo para "mueble de
 *   iglú": una silla del iglú es la misma clase, la misma colisión y el mismo sentarse
 *   que una silla del campus. Lo único propio es de dónde salen sus coordenadas.
 */
/** Nombre de matchmaking, como `SOCIAL_ROOM_NAME` y `VOLLEY_ROOM_NAME`. */
export const IGLOO_ROOM_NAME = "igloo";
export const IGLOO_FLOOR_IDS = ["baldosa", "madera", "cuadros", "cesped"];
export const IGLOO_FLOORS = {
    baldosa: { id: "baldosa", name: "Baldosa", color: "#d7dfe8", trim: "#b9c5d2" },
    madera: { id: "madera", name: "Madera", color: "#c8a06a", trim: "#a77f4c" },
    cuadros: { id: "cuadros", name: "Damero", color: "#e6ebf2", trim: "#c2cedd", cuadros: true },
    cesped: { id: "cesped", name: "Césped", color: "#9ccf90", trim: "#7fb373" },
};
export const IGLOO_WALL_IDS = ["nieve", "ladrillo", "espol", "menta"];
export const IGLOO_WALLS = {
    nieve: { id: "nieve", name: "Nieve", color: "#e8eef6", trim: "#b3c0d0" },
    ladrillo: { id: "ladrillo", name: "Ladrillo", color: "#c98f7a", trim: "#a06b58" },
    espol: { id: "espol", name: "ESPOL", color: "#1d4b78", trim: "#f5b929" },
    menta: { id: "menta", name: "Menta", color: "#bfe3d6", trim: "#8fc4b3" },
};
export const IGLOO_FLOOR_LIST = IGLOO_FLOOR_IDS.map((id) => IGLOO_FLOORS[id]);
export const IGLOO_WALL_LIST = IGLOO_WALL_IDS.map((id) => IGLOO_WALLS[id]);
/** Lo que ve un iglú que nunca se tocó. Es también el respaldo ante un dato raro. */
export const DEFAULT_IGLOO_STYLE = { floor: "baldosa", wall: "nieve" };
export function isIglooFloorId(value) {
    return typeof value === "string" && IGLOO_FLOOR_IDS.includes(value);
}
export function isIglooWallId(value) {
    return typeof value === "string" && IGLOO_WALL_IDS.includes(value);
}
/** Lo que venga de la base o de la red, convertido en un estilo que sí existe. */
export function iglooStyle(floor, wall) {
    return {
        floor: isIglooFloorId(floor) ? floor : DEFAULT_IGLOO_STYLE.floor,
        wall: isIglooWallId(wall) ? wall : DEFAULT_IGLOO_STYLE.wall,
    };
}
/**
 * La clave de arte de un iglú, con su estilo dentro.
 *
 * El estilo viaja EN la clave y no por un campo aparte porque `RoomShape` tiene un solo
 * hueco para decir cómo se dibuja un sitio (`art`), y ensancharlo sólo por el iglú
 * obligaría a las trece salas del campus a cargar con un campo que no usan. Quien pinta
 * la deshace con `parseIglooArt`.
 */
export function iglooArtKey(style = DEFAULT_IGLOO_STYLE) {
    return `igloo:${style.floor}:${style.wall}`;
}
/** De la clave al estilo. `null` si la clave no es de un iglú. */
export function parseIglooArt(key) {
    if (typeof key !== "string" || !key.startsWith("igloo:"))
        return null;
    const [, floor, wall] = key.split(":");
    return iglooStyle(floor, wall);
}
/** El id de la puerta de salida. Uno solo: cada iglú tiene exactamente una. */
export const IGLOO_EXIT_ID = "igloo-salida";
export const IGLOO_NAME = "Tu iglú";
/**
 * El piso, igual para todos los iglús.
 *
 * Uno fijo y no uno por jugador: lo que se decora es lo que hay DENTRO. Un piso
 * editable sería otro editor de mapas —con sus polígonos, su validación y su
 * reescritor— para una sala que no lo pide.
 */
export const IGLOO_FLOOR = {
    walkable: [
        { x: 90, y: 255 },
        { x: 870, y: 255 },
        { x: 870, y: 495 },
        { x: 90, y: 495 },
    ],
    spawn: { x: 480, y: 400 },
};
/** La caja de la puerta de salida, en el mismo sitio que las de "salir" del campus. */
export const IGLOO_EXIT_RECT = { x: 405, y: 410, width: 150, height: 60 };
/**
 * Cuántas piezas se pueden tener puestas a la vez.
 *
 * Existe por lo mismo que `MAX_CLUBS_PER_PLAYER`: sin tope, la sala se llena de sillas
 * hasta que no se puede caminar, y cada pieza es geometría que el servidor recorre en
 * cada paso de cada visitante.
 */
export const MAX_IGLOO_PLACED = 20;
export const FURNITURE_IDS = ["silla-basica", "mesa-basica", "planta-basica"];
export const FURNITURE = {
    "silla-basica": {
        id: "silla-basica",
        name: "Silla",
        price: 15,
        kind: "silla",
        /* 70 × 67 sale de la proporción declarada de `silla` (1.0391), no de un número
           redondo: una caja de 70 × 60 deformaría el dibujo. */
        size: { width: 70, height: 67 },
        /* El asiento cae en la mitad de abajo de la caja, que es donde se dibuja el de una
           silla vista de frente. Mismo tamaño relativo que las sillas del campus. */
        seat: {
            polygon: [
                { x: 0.28, y: 0.5 },
                { x: 0.72, y: 0.5 },
                { x: 0.72, y: 0.86 },
                { x: 0.28, y: 0.86 },
            ],
            height: 16,
        },
    },
    "mesa-basica": {
        id: "mesa-basica",
        name: "Mesa",
        price: 25,
        kind: "mesa",
        /* Igual que la silla: 140 de ancho con la proporción de `mesa` (2.0477) dan 68. */
        size: { width: 140, height: 68 },
    },
    "planta-basica": {
        id: "planta-basica",
        name: "Planta",
        price: 10,
        kind: "arbol",
        /* 70 de ancho con la proporción de `arbol` (1.0781) dan 65. Fue 70x80 mientras el
           tipo no tuvo dibujo; en cuanto lo tuvo, esa caja estiraba sprite y silueta. */
        size: { width: 70, height: 65 },
    },
};
export const FURNITURE_LIST = FURNITURE_IDS.map((id) => FURNITURE[id]);
export function isFurnitureId(value) {
    return typeof value === "string" && FURNITURE_IDS.includes(value);
}
export function getFurniture(id) {
    return isFurnitureId(id) ? FURNITURE[id] : undefined;
}
/** La caja que ocuparía este mueble puesto acá. */
export function furnitureRect(furniture, x, y) {
    return { x, y, width: furniture.size.width, height: furniture.size.height };
}
/**
 * La zona que ninguna pieza puede ocupar: la puerta de salida y el sitio donde se
 * aterriza, con el ancho del jugador de margen.
 *
 * No es cosmética. Un mueble sólido encima del umbral deja el iglú sin salida —no hay
 * dónde pararse para usarla— y uno encima del punto de llegada te mete dentro de un
 * sólido al entrar. Las dos cosas se arreglan con la misma caja porque el spawn está
 * justo encima de la puerta.
 */
export function iglooKeepOut() {
    const margin = PLAYER_RADIUS * 2;
    return {
        x: IGLOO_EXIT_RECT.x - margin,
        y: IGLOO_EXIT_RECT.y - margin,
        width: IGLOO_EXIT_RECT.width + margin * 2,
        height: IGLOO_EXIT_RECT.height + margin * 2,
    };
}
function overlaps(a, b) {
    return a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height;
}
/**
 * ¿Se puede poner este mueble acá? Devuelve el motivo, o null si sí.
 *
 * Vive en shared y no en el endpoint por lo mismo que `mapValidation.ts`: lo preguntan
 * dos lados —el servidor para decidir, el cliente para no dejar soltar la pieza en un
 * sitio que va a rebotar— y con una sola definición de "válido" no pueden discrepar.
 * La del servidor sigue siendo la que manda; la del cliente sólo ahorra el viaje.
 */
export function placementProblem(furnitureId, x, y) {
    const furniture = getFurniture(furnitureId);
    if (!furniture)
        return "ese mueble no existe";
    if (!Number.isFinite(x) || !Number.isFinite(y))
        return "la posición no es un punto";
    const rect = furnitureRect(furniture, Number(x), Number(y));
    /*
     * La caja entera dentro del mundo.
     *
     * El mobiliario SÍ puede sobresalir del piso caminable —el respaldo contra la pared es
     * lo normal, y el campus lo hace— pero fuera del lienzo no se dibuja nada: sería una
     * pieza pagada que no se ve y que igual estorba.
     */
    if (rect.x < 0 ||
        rect.y < 0 ||
        rect.x + rect.width > WORLD.width ||
        rect.y + rect.height > WORLD.height) {
        return "se sale de la pantalla";
    }
    if (overlaps(rect, iglooKeepOut()))
        return "tapa la salida";
    /*
     * Y si es un asiento, el asiento entero sobre el piso.
     *
     * Es la misma regla que `validateRoom` le aplica al campus, y por la misma razón:
     * estando encima de una superficie, ésa ES tu suelo, así que un asiento que sobresale
     * te deja de pie fuera de la sala sin que nada lo impida.
     */
    if (furniture.seat) {
        const seat = toWorld(furniture.seat.polygon, rect);
        const outside = seat.some((vertex) => !rectContains(floorBounds(), vertex));
        if (outside)
            return "el asiento queda fuera del piso";
    }
    return null;
}
/** La caja del piso. Es un rectángulo, así que basta con sus extremos. */
function floorBounds() {
    const xs = IGLOO_FLOOR.walkable.map((point) => point.x);
    const ys = IGLOO_FLOOR.walkable.map((point) => point.y);
    const x = Math.min(...xs);
    const y = Math.min(...ys);
    return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
}
export function iglooRoomId(ownerId) {
    return `igloo:${ownerId}`;
}
/** ¿Este id es el de un iglú? Lo pregunta el cliente, que no puede buscarlo en `ROOMS`. */
export function isIglooRoomId(value) {
    return typeof value === "string" && value.startsWith("igloo:");
}
/**
 * El iglú de alguien, como sala jugable.
 *
 * Puro: mismas entradas, misma sala. Lo llaman los dos lados —el servidor para simular
 * y el cliente para dibujar— y que sea la misma función es lo que garantiza que lo que
 * se ve es exactamente contra lo que se choca.
 */
/**
 * Los muebles de una colocación, como elementos del mapa.
 *
 * Sale aparte porque hay DOS cuartos decorables —el propio y el de un club— y lo único
 * que los distingue es de quién son y quién puede tocarlos. El mobiliario, el piso y la
 * puerta de salida son exactamente lo mismo, así que se arman una sola vez: dos copias
 * de esto acabarían pintando cuartos que se comportan distinto sin que nadie lo decida.
 */
function furnitureElements(placements) {
    const furniture = [];
    for (const placement of placements) {
        const def = getFurniture(placement.furnitureId);
        // Un mueble que salió del catálogo se descarta al dibujar, igual que un club
        // desconocido: la fila se queda, pero no pinta nada que nadie sepa leer.
        if (!def)
            continue;
        const rect = furnitureRect(def, placement.x, placement.y);
        if (def.kind === "silla" && def.seat) {
            furniture.push({
                id: placement.id,
                kind: "silla",
                rect,
                surface: {
                    polygon: toWorld(def.seat.polygon, rect),
                    height: def.seat.height,
                    /* Vacío es "no se sube caminando, sólo con un clic", y `fallsAtEdge` deja
                       bajarse por cualquier lado. Es el mismo trato que las diecinueve
                       superficies del campus. */
                    accessEdges: [],
                    fallsAtEdge: true,
                },
            });
            continue;
        }
        furniture.push({ id: placement.id, kind: def.kind, rect });
    }
    /*
     * El orden de la lista ES la profundidad (`elementDepths`), y acá se decide solo: por
     * borde inferior, de atrás hacia adelante.
     *
     * El campus lo elige a mano con el editor, porque ahí un mueble puede tener que tapar
     * a otro por razones de escenografía. Un cuarto decorado a clics no tiene quién tome
     * esa decisión, y derivarla de la posición es lo que todo el mundo espera de una vista
     * cenital: lo que está más abajo, está más cerca. Ordenar acá y no en la consulta es
     * lo que garantiza que el servidor y el cliente pinten la misma pila.
     */
    furniture.sort((a, b) => a.rect.y + a.rect.height - (b.rect.y + b.rect.height));
    return furniture;
}
/** La puerta de salida de un cuarto decorable: una `puerta` normal y corriente. */
function exitDoor(returnRoomId) {
    return {
        id: IGLOO_EXIT_ID,
        kind: "puerta",
        label: "Salir",
        rect: IGLOO_EXIT_RECT,
        shape: "arch",
        to: returnRoomId,
    };
}
export function buildIglooRoom(ownerId, returnRoomId, placements, style = DEFAULT_IGLOO_STYLE) {
    return {
        id: iglooRoomId(ownerId),
        name: IGLOO_NAME,
        art: iglooArtKey(style),
        walkable: IGLOO_FLOOR.walkable,
        spawn: IGLOO_FLOOR.spawn,
        // La puerta al final: el umbral se dibuja encima de lo que haya detrás.
        elements: [...furnitureElements(placements), exitDoor(returnRoomId)],
    };
}
/**
 * La sala de un CLUB: el mismo cuarto decorable, con otro dueño.
 *
 * La diferencia con el propio no está en la geometría —es idéntica— sino en de quién es
 * y quién manda: entran los miembros, decora el presidente, y lo que se pone se paga de
 * la caja común. Eso vive en el servidor; acá abajo las dos son el mismo sitio.
 *
 * Su id lleva el club dentro (`club:robotica`) por lo mismo que el del iglú lleva la
 * cuenta: el matchmaking agrupa por él, así que todos los miembros caen en la misma
 * instancia y dos clubes nunca se cruzan.
 */
export function buildClubRoom(clubId, returnRoomId, placements, style = DEFAULT_IGLOO_STYLE) {
    return {
        id: clubRoomId(clubId),
        /* Con `getClub` y no con un índice: el catálogo dejó de ser un objeto fijo, y un
           club que este proceso todavía no cargó tiene que dar un nombre pobre, no reventar. */
        name: `Sala · ${getClub(clubId)?.name ?? clubId}`,
        art: iglooArtKey(style),
        walkable: IGLOO_FLOOR.walkable,
        spawn: IGLOO_FLOOR.spawn,
        elements: [...furnitureElements(placements), exitDoor(returnRoomId)],
    };
}
/** Nombre de matchmaking de las salas de club. Ver `IGLOO_ROOM_NAME`. */
export const CLUB_ROOM_NAME = "club-room";
export function clubRoomId(clubId) {
    return `club:${clubId}`;
}
/** ¿Este id es el de la sala de un club? Lo pregunta el cliente, que no la tiene en `ROOMS`. */
export function isClubRoomId(value) {
    return typeof value === "string" && value.startsWith("club:");
}
//# sourceMappingURL=igloo.js.map